/* HuntMissionsRow — horizontale rij met alle hunt-missions van de workspace.
 * Bevat run/pause/delete acties, Zevari-campagne launcher en de provenance-
 * strip die toont welke data-sources de laatste batch voedden.
 * Geëxtraheerd uit Signals.jsx (refactor).
 */
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { toast } from "sonner";
import i18n from "i18next";
import {
    Target, Loader2, Clock, User as UserIcon, Calendar, Rocket,
    RefreshCw, Pause, Play, Trash2,
} from "lucide-react";
import ZevariCampaignModal from "@/components/ZevariCampaignModal";
import { fmtDateTime } from "./format";

export function HuntMissionsRow({ missions, activeId, onPick, onChanged }) {
    const { t } = useTranslation();
    const [runningIds, setRunningIds] = useState(() => new Set());
    const [zevariMission, setZevariMission] = useState(null);
    if (!missions || missions.length === 0) return null;

    const toggle = async (m) => {
        try { await api.post(`/hunt-missions/${m.id}/toggle`); onChanged?.(); }
        catch (e) { toast.error(e?.response?.data?.detail || i18n.t("common.failed")); }
    };
    const runNow = async (m) => {
        setRunningIds((s) => new Set(s).add(m.id));
        toast.info(i18n.t("missions.running_toast", { title: m.title }));
        try {
            const r = await api.post(`/hunt-missions/${m.id}/run`);
            toast.success(r.data?.summary || i18n.t("missions.run_done"));
            onChanged?.();
        } catch (e) { toast.error(e?.response?.data?.detail || i18n.t("common.failed")); }
        finally {
            setRunningIds((s) => { const n = new Set(s); n.delete(m.id); return n; });
        }
    };
    const remove = async (m) => {
        if (!confirm(i18n.t("missions.delete_confirm", { title: m.title }))) return;
        try { await api.delete(`/hunt-missions/${m.id}`); toast.success(i18n.t("common.deleted")); onChanged?.(); }
        catch (e) { toast.error(e?.response?.data?.detail || i18n.t("common.failed")); }
    };

    return (
        <div className="rounded-xl border border-white/8 bg-[var(--jr-surface)] p-4 mb-4" data-testid="hunt-missions-row">
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                    <Target className="text-[var(--jr-brand)]" size={14} />
                    <span className="text-[10px] font-mono uppercase tracking-[0.22em] text-[var(--jr-text-3)]">Hunt missions</span>
                    <span className="text-[10px] font-mono text-[var(--jr-text-3)]">· {missions.length}</span>
                </div>
            </div>
            <div className="flex gap-2 overflow-x-auto pb-1">
                {missions.map((m) => {
                    const isActive = activeId === m.id;
                    const paused = m.status === "paused";
                    const running = runningIds.has(m.id);
                    return (
                        <div key={m.id} data-testid={`mission-${m.id}`}
                            className={`shrink-0 w-72 rounded-lg border p-3 relative transition-colors ${running ? "border-[var(--jr-brand)] bg-[var(--jr-brand)]/12 shadow-[0_0_0_3px_rgba(255,140,42,0.14)]" : isActive ? "border-[var(--jr-brand)] bg-[var(--jr-brand)]/8" : paused ? "border-white/8 bg-[var(--jr-surface-2)]/50 opacity-60" : "border-white/10 bg-[var(--jr-surface-2)]"}`}>
                            {running && (
                                <div className="absolute top-2 right-2 inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-[0.18em] px-1.5 py-0.5 rounded-full bg-[var(--jr-brand)] text-black"
                                    data-testid={`mission-running-${m.id}`}>
                                    <Loader2 className="animate-spin" size={9}/> Hunt loopt
                                </div>
                            )}
                            <div className="flex items-start justify-between gap-2">
                                <button type="button" onClick={() => onPick(isActive ? "" : m.id)} className="text-left min-w-0 flex-1">
                                    <div className="text-sm font-medium text-white truncate">{m.title}</div>
                                    <div className="text-[10px] text-[var(--jr-text-3)] truncate">{m.target_role || "—"} · {m.target_sector || "—"} · {m.target_country || "—"}</div>
                                </button>
                                <span className={`text-[9px] font-mono uppercase tracking-[0.16em] px-1.5 py-0.5 rounded ${m.schedule === "daily" ? "bg-[var(--jr-info)]/15 text-[var(--jr-info)]" : m.schedule === "weekly" ? "bg-[var(--jr-amber)]/15 text-[var(--jr-amber)]" : "bg-white/5 text-[var(--jr-text-3)]"}`}>{m.schedule}</span>
                            </div>
                            <div className="mt-2 text-[10px] text-[var(--jr-text-3)] line-clamp-2">{m.last_run_summary || i18n.t("missions.never_run")}</div>
                            {/* When + who — date AND time of the last hunt plus the human
                                (or scheduler-agent) that performed it. */}
                            <div className="mt-1.5 space-y-0.5" data-testid={`mission-meta-${m.id}`}>
                                {m.last_run_at && (
                                    <div className="flex items-center gap-1.5 text-[10px] font-mono text-[var(--jr-text-3)]">
                                        <Clock size={9} className="shrink-0"/>
                                        <span className="text-[var(--jr-text-2)]">{t("signals.mission_last_run")}:</span>
                                        <span>{fmtDateTime(m.last_run_at)}</span>
                                        <UserIcon size={9} className="shrink-0 ml-0.5"/>
                                        <span className="truncate">{m.last_run_scheduled ? t("signals.by_agent") : (m.last_run_by_name || m.created_by_name || "—")}</span>
                                    </div>
                                )}
                                {m.created_at && (
                                    <div className="flex items-center gap-1.5 text-[10px] font-mono text-[var(--jr-text-3)]/80">
                                        <Calendar size={9} className="shrink-0"/>
                                        <span>{t("signals.mission_created")}: {fmtDateTime(m.created_at)}</span>
                                        {m.created_by_name && <span className="truncate">· {m.created_by_name}</span>}
                                    </div>
                                )}
                            </div>
                            {/* Provenance strip — visible per mission so you know at
                                a glance which sources fed this batch. Multi-select
                                missions show every source in order. */}
                            <MissionProvenance mission={m} />
                            {/* Zevari campaign badge — this mission's contacts feed a
                                LinkedIn campaign staged behind Zevari's approval gate */}
                            {m.zevari_campaign && (
                                <button onClick={() => setZevariMission(m)} data-testid={`mission-zevari-badge-${m.id}`}
                                    className={`mt-1.5 inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-[0.14em] px-1.5 py-0.5 rounded border transition-colors ${
                                        m.zevari_campaign.status === "active"
                                            ? "border-[var(--jr-success)]/40 bg-[var(--jr-success)]/10 text-[var(--jr-success)]"
                                            : "border-[#38bdf8]/40 bg-[#38bdf8]/10 text-[#38bdf8] hover:bg-[#38bdf8]/20"}`}>
                                    <Rocket size={9} />
                                    Zevari · {m.zevari_campaign.status === "active" ? t("signals.zev_status_active") : t("signals.zev_status_pending")}
                                </button>
                            )}
                            <div className="mt-2 pt-2 border-t border-white/8 flex items-center justify-between gap-2">
                                <div className="text-[10px] font-mono text-[var(--jr-text-3)]">
                                    {m.signals_generated} signalen
                                </div>
                                <div className="flex items-center gap-1">
                                    {/* Campagne via Zevari — hunted contacts → approval-gated
                                        LinkedIn warming/outreach campaign */}
                                    <button onClick={() => setZevariMission(m)} data-testid={`mission-zevari-${m.id}`}
                                        title={t("signals.zev_btn_title")}
                                        className="p-1 rounded hover:bg-[#38bdf8]/10 text-[var(--jr-text-2)] hover:text-[#38bdf8]">
                                        <Rocket size={11} />
                                    </button>
                                    <button onClick={() => runNow(m)} title={running ? "Hunt loopt…" : "Run nu"} data-testid={`mission-run-${m.id}`}
                                        disabled={running}
                                        className="p-1 rounded hover:bg-white/5 text-[var(--jr-text-2)] hover:text-[var(--jr-brand)] disabled:opacity-50 disabled:cursor-not-allowed">
                                        {running ? <Loader2 size={11} className="animate-spin text-[var(--jr-brand)]"/> : <RefreshCw size={11} />}
                                    </button>
                                    <button onClick={() => toggle(m)} title={paused ? i18n.t("missions.activate") : i18n.t("missions.pause")} data-testid={`mission-toggle-${m.id}`}
                                        className="p-1 rounded hover:bg-white/5 text-[var(--jr-text-2)] hover:text-white">
                                        {paused ? <Play size={11} /> : <Pause size={11} />}
                                    </button>
                                    <button onClick={() => remove(m)} title={i18n.t("common.delete")} data-testid={`mission-delete-${m.id}`}
                                        className="p-1 rounded hover:bg-[var(--jr-error)]/10 text-[var(--jr-text-3)] hover:text-[var(--jr-error)]"><Trash2 size={11} /></button>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
            {zevariMission && (
                <ZevariCampaignModal mission={zevariMission}
                    onClose={() => setZevariMission(null)}
                    onChanged={onChanged} />
            )}
        </div>
    );
}

/* ------------------------------------------------------------------ */
/*  Mission provenance strip — shows which data-sources fed this batch */
/* ------------------------------------------------------------------ */
const SOURCE_META = {
    llm:           { label: "LLM",           color: "#94a3b8" },
    apollo:        { label: "Apollo",        color: "#3b82f6" },
    perplexity:    { label: "Perplexity",    color: "#ff5a00" },
    serper:        { label: "Serper",        color: "#22c55e" },
    linkedin_jobs: { label: "LinkedIn Jobs", color: "#4c8bf5" },
    apify_leads: { label: "Apify Leads", color: "#97d700" },
    apify_jobs: { label: "LinkedIn Jobs (Apify)", color: "#97d700" },
    google_maps: { label: "Google Maps", color: "#34a853" },
    zevari: { label: "Zevari Signal-scan", color: "#0a66c2" },
    scaliq: { label: "ScalIQ People-search", color: "#6366f1" },
};

function MissionProvenance({ mission }) {
    // Prefer the new multi-source field; fall back to legacy single value.
    const sources = (mission.data_sources && mission.data_sources.length > 0)
        ? mission.data_sources
        : [mission.data_source || "llm"];
    // De-duplicate for display (case where legacy + new both write the same value)
    const unique = Array.from(new Set(sources));
    return (
        <div className="mt-2 flex items-center gap-1.5 flex-wrap" data-testid={`mission-provenance-${mission.id}`}>
            <span className="text-[9px] font-mono uppercase tracking-[0.16em] text-[var(--jr-text-3)]">Bronnen:</span>
            {unique.map((src) => {
                const meta = SOURCE_META[src] || { label: src, color: "#94a3b8" };
                return (
                    <span key={src} title={`Data-source: ${meta.label}`}
                        data-testid={`mission-src-${mission.id}-${src}`}
                        style={{ background: `${meta.color}20`, color: meta.color, borderColor: `${meta.color}55` }}
                        className="inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-[0.14em] px-1.5 py-0.5 rounded border">
                        <span className="w-1.5 h-1.5 rounded-full" style={{ background: meta.color }} />
                        {meta.label}
                    </span>
                );
            })}
            {mission.mission_type && mission.mission_type !== "growth_signals" && (
                <span className="text-[9px] font-mono uppercase tracking-[0.14em] px-1.5 py-0.5 rounded border border-white/15 bg-white/5 text-[var(--jr-text-2)]">
                    {mission.mission_type === "vacancy_burst" ? i18n.t("sigcard.vacancy_hunt") : i18n.t("sigcard.cand_to_vac")}
                </span>
            )}
        </div>
    );
}
